import {
  Component,
  computed,
  inject,
  INJECTOR,
  input,
  linkedSignal,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  IconComponent,
  ResolvableModal,
  TabComponent,
  TabGroupComponent,
  TranslatePipe,
} from '@softline/ui-core';
import { Bewe, Bewelg } from '../../types/bewegung';
import { handleRequestError, MengenEingabeComponent } from '@softline/application';
import { Store } from '@softline/core';
import { SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_BEWE } from '../../warenuebernahme.shared';
import { WwsBewegungStore } from '../../store/bewegung.store';
import { Warenuebernahme } from '../../types/warenuebernahme';
import { MischPalettenService } from '../../services/mischpaletten-service';
import { MischPalette } from '../../types/misch-paletten';
import { BewegungenService } from '../../services/bewegungen-service';
import { firstValueFrom } from 'rxjs';
import { WwsWarenuebernahmeStore } from '../../store/warenuebernahme.store';

@Component({
  selector: 'soft-position-erledigen-dialog',
  imports: [
    CommonModule,
    MengenEingabeComponent,
    IconComponent,
    TabComponent,
    TabGroupComponent,
    TranslatePipe,
  ],
  templateUrl: './position-erledigen-dialog.component.html',
  styleUrls: ['./position-erledigen-dialog.component.css'],
  providers: [MischPalettenService],
})
export class PositionErledigenDialogComponent
  extends ResolvableModal<null | 'kopf' | 'bewegung'>
  implements OnInit
{
  readonly #injector = inject(INJECTOR);

  // public anzahlEtiketten = 0;
  public bewe = input.required<Bewe>();
  public bewelg = input<Bewelg>();
  public warenuebernahme = input.required<Warenuebernahme>();

  readonly selectableMischPaletten = signal<MischPalette[] | null>(null);
  readonly selectedMischPalette = signal<MischPalette | null>(null);

  readonly mischPalettenService = inject(MischPalettenService);
  private readonly bewegungenService = inject(BewegungenService);

  readonly anzahlEtiketten = linkedSignal(() => this.bewe()?.anzahlPalettenMin ?? 0);

  readonly rowsForSplitting = linkedSignal(() => {
    const count = this.anzahlEtiketten();
    const bewelg = this.bewelg();

    const quantity = (bewelg ? bewelg.liefmenge : this.bewe()?.artikelMenge?.menge) ?? 0;

    const rest = quantity % count;
    const amountPerPallet = (quantity - rest) / count;

    return Array.from(Array(count).keys()).map((index, i) => ({
      amount: i === 0 ? amountPerPallet + rest : amountPerPallet,
      index,
    }));
  });

  readonly splittingSum = computed(() =>
    this.rowsForSplitting().reduce((sum, row) => sum + row.amount, 0)
  );

  constructor(private store: Store) {
    super();
  }

  async ngOnInit() {
    const result = await this.mischPalettenService.postionErledigenMischpaletten(
      this.warenuebernahme().id
    );

    this.selectableMischPaletten.set(result.result);
  }

  // set bewegung(bewegung: Bewe) {
  //   this.anzahlEtiketten = bewegung?.anzahlPalettenMin ?? 0;
  //   this.bewe = bewegung;
  // }
  //
  // set warenuebernahme(warenuebernahme: Warenuebernahme) {
  //   this._warenuebernahme = warenuebernahme;
  // }

  async positionErledigen(anzahlEtiketten: number) {
    const bewelg = this.bewelg();

    if (bewelg) {
      const updatedBeweLg = await firstValueFrom(
        this.bewegungenService.positionErledigenLademittel({
          anzahlEtiketten: anzahlEtiketten,
          bewelgBean: bewelg,
          splittung: this.rowsForSplitting().map((row) => row.amount),
        })
      );

      const updatedBeweLgs = [...(this.bewe().bewelgs ?? [])];
      const index = updatedBeweLgs.findIndex(
        (o) => o.id === updatedBeweLg.id && this.bewe().id === updatedBeweLg.idbewe
      );

      if (index < 0) {
        return;
      }

      updatedBeweLgs[index] = { ...updatedBeweLg };

      this.store.commit(
        SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_BEWE,
        WwsBewegungStore.mutations.patch,
        {
          id: this.bewe().id,
          changes: {
            bewelgs: updatedBeweLgs,
          },
        }
      );

      await this.store.dispatch(
        SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_BEWE,
        WwsBewegungStore.actions.loadMany,
        { pathParams: { idwu: this.warenuebernahme().id }, clear: true }
      );

      this.resolve('bewegung');
    } else {
      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_BEWE,
        WwsBewegungStore.actions.positionErledigen,
        {
          warenuebernahme: this.warenuebernahme(),
          payload: {
            anzahlEtiketten: anzahlEtiketten,
            bewegung: this.bewe(),
            splittung: this.rowsForSplitting().map((row) => row.amount),
          },
        }
      );

      this.resolve(result);
    }
  }

  async positionErledigenMischPaeltte() {
    const palette = this.selectedMischPalette()?.palette;
    if (!palette) return;

    const bewelg = this.bewelg();

    if (bewelg) {
      try {
        const updatedBeweLgPal = await firstValueFrom(
          this.bewegungenService.positionErledigenMischPaletteLademittel({
            palette,
            bewelg,
          })
        );

        const updatedBeweLg = updatedBeweLgPal?.bewelg;

        const updatedBeweLgs = [...(this.bewe().bewelgs ?? [])];
        const index = updatedBeweLgs.findIndex(
          (o) => o.id === updatedBeweLg.id && this.bewe().id === updatedBeweLg.idbewe
        );

        if (index < 0) {
          return;
        }

        updatedBeweLgs[index] = { ...updatedBeweLg };

        this.store.commit(
          SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_BEWE,
          WwsBewegungStore.mutations.patch,
          {
            id: this.bewe().id,
            changes: {
              bewelgs: updatedBeweLgs,
            },
          }
        );

        this.resolve('bewegung');
      } catch (e) {
        handleRequestError(e, this.#injector, { show: true });
      }
    } else {
      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_BEWE,
        WwsBewegungStore.actions.positionErledigenMischPalette,
        {
          warenuebernahme: this.warenuebernahme(),
          payload: {
            palette: palette,
            bewegung: this.bewe(),
          },
        }
      );
      this.resolve(result);
    }
  }

  changePalletAmount(index: number, amount: number) {
    const array = this.rowsForSplitting();
    array.splice(index, 1, { index, amount });
    this.rowsForSplitting.set([...array]);
  }
}
